import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Home.scss';

interface FaqItem {
  question: string;
  answer: React.ReactNode;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    question: 'When and where is the wedding?',
    answer:
      'The ceremony will take place on 1st September 2026 at 2:00 pm at The Waiting Room, Eaglescliffe, with the reception to follow at the same venue.',
  },
  {
    question: 'How do I RSVP?',
    answer:
      'Using the code on your invitation by 1st August.',
  },
  {
    question: 'Is there a dress code?',
    answer:
      'We\'d love for guests to dress in smart/formal attire. We\'re asking guests to avoid wearing white or ivory out of respect for the bride.',
  },
  {
    question: 'Are children welcome?',
    answer:
      'We love your little ones! Children are welcome at both the ceremony and the reception. Please let us know when you RSVP so we can ensure seating is arranged.',
  },
  {
    question: 'Will there be parking available?',
    answer:
      'There is limited parking at the venue. We recommend that you take a taxi. Station Cars is located very close to the venue and can be contacted on 01642 788788.',
  },
  {
    question: 'What time should I arrive?',
    answer:
      'Please arrive at 1:15 pm to allow plenty of time before the ceremony commences.',
  },
  {
    question: 'Do you have a gift list?',
    answer:
      'Your presence is truly the greatest gift of all. However, should you wish to contribute, a monetary gift to help us celebrate would be warmly received.',
  },
  {
    question: 'Will there be accommodation nearby?',
    answer: ['For guests travelling to celebrate with us, there are plenty of accommodation options within easy reach of the venue.',
    <br />,
    <br />,
    <strong>Budget-Friendly Options</strong>,
    <ul style={{ paddingLeft: '20px' }}>
      <li>Outram 26 — Simple and affordable rooms in Stockton town centre, around a 10-15 minute walk from the venue.</li>
      <li>The Hoptimist, Stockton — A budget-friendly option close to the town centre with easy access to local bars and restaurants.</li>
      <li>Premier Inn Stockton-On-Tees (Preston Farm) hotel — Reliable and comfortable, around a 5-10 minute taxi ride away with free parking nearby.</li>
    </ul>,
    <br />,
    <strong>Mid-Range Options</strong>,
    <ul style={{ paddingLeft: '20px' }}>
      <li>Hampton by Hilton Stockton on Tees — Modern riverside hotel with breakfast included and only a short walk/taxi from the venue. Great option for guests wanting something central and convenient.</li>
      <li>Locomotion hotel — A cosy and characterful option in Eaglescliffe, close to Yarm and Stockton.</li>
      <li>Premier Inn Stockton-on-Tees (Teesside) hotel — Comfortable and family-friendly with good transport links.</li>
    </ul>,
    <br />,
    <strong>A Little More Luxurious</strong>,
    <ul style={{ paddingLeft: '20px' }}>
      <li>Abbeyfield Lodge Guest House & B&B Yarm — Boutique-style accommodation in nearby Yarm with a lovely relaxed feel.</li>
      <li>Parkmore Hotel — A stylish hotel with spa and leisure facilities, ideal if you’d like to make a weekend of it.</li>
      <li>The Stables at the Vale — Beautiful countryside accommodation just outside Stockton, perfect for a quieter and more luxurious stay.</li>
    </ul>,
    <br />,
    'We’d recommend booking accommodation sooner rather than later, especially if you’re planning to stay over on the Monday night too.'
    ]
  },
];

const WeddingFaqPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) =>
    setOpenIndex(prevOpenIndex => (prevOpenIndex === i ? null : i));

  return (
    <div className="wedding" data-theme="alexlibby">
      <header className="wedding__hero">
        <p className="wedding__hero-eyebrow">We&rsquo;re getting married</p>
        <h1 className="wedding__hero-names">Alex &amp; Libby</h1>
        <p className="wedding__hero-date">1st September 2026 &nbsp;&bull;&nbsp; The Waiting Room, Eaglescliffe</p>
        <a href="/rsvp" className="wedding__hero-rsvp-btn">
          RSVP
        </a>
      </header>

      <main className="wedding__main">
        <section className="wedding__faq">
          <div className="wedding__container">
            <h2 className="wedding__section-title">Frequently Asked Questions</h2>
            <p className="wedding__section-subtitle">
              Everything you need to know about our big day. Can&rsquo;t find an answer?
              Don&rsquo;t hesitate to get in touch.
            </p>
            <ul className="wedding__faq-list">
              {FAQ_ITEMS.map((item, i) => (
                <li key={item.question} className={`wedding__faq-item${openIndex === i ? ' wedding__faq-item--open' : ''}`}>
                  <button
                    type="button"
                    className="wedding__faq-question"
                    onClick={() => toggle(i)}
                    aria-expanded={openIndex === i}
                  >
                    {item.question}
                    <span className="wedding__faq-icon" aria-hidden="true">
                      {openIndex === i ? '−' : '+'}
                    </span>
                  </button>
                  {openIndex === i && (
                    <div className="wedding__faq-answer">
                      <p>{item.answer}</p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <footer className="wedding__footer">
        <p>With love, Alex &amp; Libby &hearts;</p>
      </footer>
    </div>
  );
};

const Home: React.FC = () => {
  const hostname        = window.location.hostname;
  const isAlexLibbySite = hostname === 'www.alexlibbywedding.co.uk';

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://use.typekit.net/jbu1zjh.css';
    if (isAlexLibbySite) {
      link.href = 'https://use.typekit.net/ydl0lfp.css';
    }
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, [isAlexLibbySite]);

  const { isAuthenticated } = useAuth();

  if (isAlexLibbySite) {
    return <WeddingFaqPage />;
  }

  return (
    <div className="home">
      <div className="home__hero">
        <div className="container">
          <h1 className="home__title">Welcome to RSVP App</h1>
          <p className="home__subtitle">
            Manage your events and track RSVPs with ease
          </p>
          <div className="home__actions">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="home__btn home__btn--primary">
                  Go to Dashboard
                </Link>
                <Link to="/events" className="home__btn home__btn--secondary">
                  View Events
                </Link>
              </>
            ) : (
              <>
                <Link to="/register" className="home__btn home__btn--primary">
                  Get Started
                </Link>
                <Link to="/rsvp" className="home__btn home__btn--secondary">
                  RSVP to Event
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="home__features">
        <div className="container">
          <h2 className="text-center mb-lg">Features</h2>
          <div className="home__feature-grid">
            <div className="home__feature">
              <div className="home__feature-icon">📅</div>
              <h3>Event Management</h3>
              <p>Create and manage multiple events with ease</p>
            </div>
            <div className="home__feature">
              <div className="home__feature-icon">👥</div>
              <h3>Guest Lists</h3>
              <p>Track your guests and their RSVP status</p>
            </div>
            <div className="home__feature">
              <div className="home__feature-icon">🔗</div>
              <h3>Easy RSVP</h3>
              <p>Guests can RSVP using simple shortcodes</p>
            </div>
            <div className="home__feature">
              <div className="home__feature-icon">📊</div>
              <h3>Real-time Updates</h3>
              <p>See RSVP responses as they come in</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;

